import React, { useEffect, useState } from 'react';
import './blogPage.scss';
import PagesHeroSection from '../../components/pagesHeroSection/pagesHeroSection';
import emptyBlogImg from '../../assets/blog/no-blog-img.png';
import blogBg from '../../assets/blog/blogbg.jpeg';

import { collection, getDocs } from 'firebase/firestore/lite';
import { db } from '../../firebase/firebase';

import { Link, useParams } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import SEOUpdater, { CANONICAL_ORIGIN } from '../../utils/hoc/SEOUpdater';
import { useTranslation } from 'react-i18next';

const BlogPage = () => {
  const { t } = useTranslation('blogPage');

  const { lang } = useParams();

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'blogs'));

        const blogList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setBlogs(blogList);
      } catch (err) {
        console.error('Error loading blogs:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Avagyan Law Firm Legal Blog',
    description:
      'Legal insights and updates covering immigration law, personal injury, lemon law, and workers’ compensation from Avagyan Law Firm.',
    // Fixed origin, not window.location.href - the app is reachable at
    // multiple hosts (see SEOUpdater.jsx) and react-snap's headless crawler
    // runs against a localhost dev server, so window.location.href would
    // bake an unstable or literally "http://localhost:PORT/..." URL into
    // this structured data depending on when/where it's captured.
    url: `${CANONICAL_ORIGIN}${window.location.pathname}`,
    publisher: {
      '@type': 'Organization',
      name: 'Avagyan Law Firm',
    },
  };

  return (
    <div className="blog-page">
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      {/* Dynamic SEO metadata */}
      <SEOUpdater page="blogPage" />

      <PagesHeroSection page={t('heroTitle')} background={blogBg} />

      {/*
        `loading` used to be an early return with an entirely different
        outer shape (no .blog-page wrapper, no SEOUpdater, no schema
        script) before this <Container> was ever reached. react-snap always
        waits for the Firestore fetch to settle before it captures a page,
        so the prerendered HTML never contains that loading shape - it's
        always this same .blog-page/Container structure, in whichever of
        the error / empty / list states the fetch settled into. A fresh
        visitor's first render used to start from the loading shape every
        time, which is a structural mismatch hydration can't reconcile
        ("Minified React error #418") on every single blog page load.
        Keeping the same outer wrapper for every state - loading included -
        and only swapping the inner content removes that structural
        mismatch entirely.

        One narrower, already-known gap remains, documented in
        scripts/inject-blog-content.js: this component's own Firestore
        fetch fails inside react-snap's headless browser, so react-snap's
        own capture always settles into the empty/error state below, never
        a real post list - inject-blog-content.js separately fetches the
        same data over plain HTTPS at build time and hand-writes a real
        post list into the static file afterward, specifically so the
        blog's links stay crawlable. That hand-written list still won't
        byte-for-byte match what THIS component renders once its own
        client-side fetch resolves, so on a production build with real
        published posts, the post list itself (not the whole page) can
        still trigger one soft, self-correcting hydration mismatch on
        first load - React discards and re-renders that portion
        client-side, so a visitor still sees the correct list. Fully
        closing that gap means the client would need to read the same
        build-time-fetched data (e.g. embedded as JSON) instead of
        re-fetching independently, which is a larger change than this fix.
      */}
      <Container className="py-5">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">{t('loadingBlogs')}</p>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-5">
            <h2>{t('errorTitle')}</h2>
            <p>{t('errorDescription')}</p>
          </div>
        )}

        {!loading && !error && blogs.length === 0 && (
          <div className="text-center py-5">
            <h2>{t('noBlogs')}</h2>
            <p>{t('noBlogsDescription')}</p>
          </div>
        )}

        <Row>
          {blogs.map(blog => {
            const title = blog.title?.[lang] || blog.title?.en || 'Blog Article';

            const image = blog.imgUrl && blog.imgUrl.trim().length ? blog.imgUrl : emptyBlogImg;

            return (
              <Col lg={6} key={blog.id} className="blog-card mb-4">
                <article className="blog-content">
                  <Row>
                    <Col sm={4} xs={5}>
                      <Link to={`/${lang}/blog/${blog.slug}`} className="blog-image-wrapper">
                        <img src={image} alt={title} loading="lazy" />
                      </Link>
                    </Col>

                    <Col sm={8} xs={7}>
                      <h3 className="blog-title">
                        <Link to={`/${lang}/blog/${blog.slug}`}>{title}</Link>
                      </h3>

                      <Link to={`/${lang}/blog/${blog.slug}`} className="blog-read-more-btn">
                        {t('readMore')}
                      </Link>
                    </Col>
                  </Row>
                </article>
              </Col>
            );
          })}
        </Row>
      </Container>
    </div>
  );
};

export default BlogPage;
